@layer refine.base {
  :host {
    --_bg: var(--carousel-bg, var(--color-canvas));
    --_radius: var(--carousel-radius, var(--rounded-xl));
    --_dot-bg: var(--carousel-dot-bg, var(--color-contrast-300));
    --_dot-active-bg: var(--carousel-dot-active-bg, var(--color-contrast-700));

    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: var(--carousel-min-height, 240px);
    overflow: hidden;
    touch-action: auto;
    outline: none;
    background: var(--_bg);
    border-radius: var(--_radius);
  }

  /* ── Track ──────────────────────────────────────────────────────── */

  .track {
    position: relative;
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
  }

  /* ── Controls (prev / next) ────────────────────────────────────────────── */

  /* Controls live inside the bottom nav bar, right-aligned via margin-inline-start: auto. */

  .controls {
    display: flex;
    gap: var(--size-0-5);
    align-items: center;
    margin-inline-start: auto;
    pointer-events: none;
  }

  /* Controls rendered standalone (no indicators bar) — needs own positioning + backdrop. */
  :host > .controls {
    position: absolute;
    inset-block-end: var(--size-3);
    inset-inline-end: var(--size-3);
    padding: var(--size-1-5) var(--size-2);
    margin-inline-start: 0;
    background: color-mix(in oklch, var(--color-canvas) 55%, transparent);
    border-radius: var(--rounded-full);
    backdrop-filter: blur(6px);
  }

  .nav-btn {
    pointer-events: auto;
  }

  /* ── Variant-specific track styles ────────────────────────────────────── */

  /* Gallery: flex row, fills host, shows active + two thumbnails */
  :host([variant='gallery']) .track {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: row;
    gap: var(--carousel-gallery-gap, var(--size-2));
    align-items: stretch;
    overflow: hidden;
  }

  :host([variant='gallery'][orientation='vertical']) .track {
    flex-direction: column;
    align-items: stretch;
  }

  /* Filmstrip: flex row/column with absolute positioning to fill host */
  :host([variant='filmstrip']) .track {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: row;
    gap: var(--carousel-filmstrip-gap, var(--size-2));
    align-items: stretch;
    overflow: hidden;
  }

  :host([variant='filmstrip'][orientation='vertical']) .track {
    flex-direction: column;
    width: 100%;
  }

  /* ── Vertical orientation ──────────────────────────────────────────────── */

  :host([orientation='vertical']) {
    --_slide-translate-before: translateY(-100%);
    --_slide-translate-after: translateY(100%);
  }

  :host([orientation='vertical']) .indicators {
    inset-block: 50% auto;
    inset-inline: var(--size-3) auto;
    flex-direction: column;
    width: auto;
    height: max-content;
    padding-block: var(--size-6) var(--size-2);
    padding-inline: var(--size-1-5);
    border-radius: var(--rounded-full);
    transform: translateX(0) translateY(-50%);
  }

  :host([orientation='vertical']) .controls {
    flex-direction: column;
    margin-block-start: auto;
    margin-inline-start: 0;
  }

  :host([orientation='vertical']) > .controls {
    inset-block-end: var(--size-3);
    inset-inline-end: var(--size-3);
    margin-block-start: 0;
  }

  :host([orientation='vertical']) .indicator:not(.indicator-active) {
    width: var(--size-2);
    height: var(--size-2);
  }

  :host([orientation='vertical']) .indicator-active {
    width: var(--size-1-5);
    height: var(--size-8);
  }

  /* animation properties are inherited from .indicator-active ore-progress::part(fill) below */

  /* ── Bottom nav bar: indicators + controls ───────────────────────────── */

  /* Floats above the bottom edge, shrinks to content width, centered.
     A frosted backdrop keeps it legible over any image. */

  .indicators {
    position: absolute;
    inset-block-end: var(--size-3);
    inset-inline: 50%;
    display: flex;
    flex-direction: row;
    gap: var(--size-1-5);
    align-items: center;
    width: max-content;
    padding: var(--size-1-5) var(--size-2) var(--size-1-5) var(--size-6);
    pointer-events: none;
    background: color-mix(in oklch, var(--color-canvas) 55%, transparent);
    border-radius: var(--rounded-full);
    backdrop-filter: blur(6px);
    transform: translateX(-50%);
  }

  .indicator {
    display: block;
    flex-shrink: 0;
    padding: 0;
    pointer-events: auto;
    cursor: pointer;
    background: none;
    border: none;
    transition: opacity var(--transition-fast);
  }

  .indicator > ore-progress {
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  /* Inactive indicator: small circle */
  .indicator:not(.indicator-active) {
    width: var(--size-2);
    height: var(--size-2);
    overflow: hidden;
    border-radius: var(--rounded-full);
  }

  /* Active indicator: wider pill (progress bar) */
  .indicator-active {
    --progress-track-bg: color-mix(in oklch, var(--_dot-bg) 60%, transparent);
    width: var(--size-8);
  }

  .indicator:focus-visible {
    outline: var(--border) solid currentcolor;
    outline-offset: 2px;
  }

  .indicator:hover {
    opacity: 0.8;
  }

  .indicator ore-progress::part(fill) {
    transition: width var(--transition-normal);
  }

  .indicator-active ore-progress::part(fill) {
    animation-name: var(--carousel-animation-name, none);
    animation-duration: calc(var(--carousel-timeout, 4000) * 1ms);
    animation-timing-function: var(--ease-linear);
    animation-fill-mode: forwards;
  }
}

@media (prefers-reduced-motion: reduce) {
  :host {
    --carousel-transition-duration: 0s;
  }
}

@keyframes carousel-fill {
  from {
    width: 0%;
  }

  to {
    width: 100%;
  }
}

@keyframes carousel-fill-v {
  from {
    height: 0%;
  }

  to {
    height: 100%;
  }
}
